import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useSelectedBot } from "../contexts/SelectedBotContext.js";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.js";
import "./sectionPages.css";
import "./Billing.css";

// Workspace-level billing overview: one card per agent. Each card shows the
// agent's current plan + status and a "Manage billing" button that opens the
// per-agent billing page (/billing/:botId) where the pricing tiers (or the
// Wix-managed view) live. This is an account-level page — it does not depend on
// a selected bot.
function Billing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const { bots, loading, setSelectedBotId } = useSelectedBot();

  // Selecting an agent's billing also makes it the active agent, so the header
  // bot selector (and the rest of the app) follow the agent being billed.
  const manage = (botId) =>
    requestUnsavedAction(() => {
      setSelectedBotId(String(botId));
      navigate(`/billing/${botId}`);
    });

  if (loading) {
    return (
      <div className="billing-page">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <header className="section-head">
        <h1 className="section-head-title">{t("billing_page_title")}</h1>
        <p className="section-head-sub">
          {t("billing_page_subtitle")}
        </p>
      </header>

      {(!bots || bots.length === 0) ? (
        <div className="billing-empty">
          <p>{t("billing_no_agents")}</p>
          <button
            type="button"
            className="billing-empty-cta"
            onClick={() => requestUnsavedAction(() => navigate("/my-bots"))}
          >
            {t("billing_go_to_agents")}
          </button>
        </div>
      ) : (
        <div className="billing-agent-grid">
          {bots.map((bot) => {
            const planName = bot.subscription_plan?.name || "Free";
            const status = bot.status_display; // { text, className } | null
            const isWix = !!(bot.wix_instance_id || bot.wix_product);
            const displayName = bot.name || bot.domain_name || t("billing_agent_fallback_name");
            const initial = (displayName.trim().charAt(0) || "A").toUpperCase();

            return (
              <div key={bot.id} className="billing-agent-card">
                <div className="billing-agent-top">
                  <span className="billing-agent-avatar">{initial}</span>
                  <div className="billing-agent-headings">
                    <p className="billing-agent-name" title={displayName}>{displayName}</p>
                    {isWix && <span className="billing-agent-provider">{t("billing_via_wix")}</span>}
                  </div>
                </div>

                <div className="billing-agent-meta">
                  <span className="billing-agent-plan">{planName}</span>
                  {status ? (
                    <span className={`billing-agent-status ${status.className}`}>
                      <span className="billing-agent-status-dot" />
                      {status.text}
                    </span>
                  ) : (
                    <span className="billing-agent-status free">
                      <span className="billing-agent-status-dot" />
                      {t("billing_free_plan")}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="billing-manage-btn"
                  onClick={() => manage(bot.id)}
                >
                  {t("billing_manage")}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Billing;
